import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type {
  HostingPackageDto,
  ProvisioningUrlRequest,
  ProvisioningUrlResult
} from "@/types/api";

/**
 * Live 20i package info for a container — drives the Hosting panel on
 * Overview. Only fetches when the container has a TwentyIPackageId
 * (otherwise the Add-Hosting CTA card renders instead).
 */
export function useHostingInfo(containerId: number | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ["hosting", containerId],
    enabled: typeof containerId === "number" && enabled,
    queryFn: ({ signal }) =>
      api.get<HostingPackageDto>(`/api/containers/${containerId}/hosting`, signal),
    staleTime: 30_000
  });
}

export function useSuspendHosting(containerId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post(`/api/containers/${containerId}/hosting/suspend`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["hosting", containerId] });
      qc.invalidateQueries({ queryKey: ["containers", containerId] });
      qc.invalidateQueries({ queryKey: ["containers"] });
    }
  });
}

export function useRestoreHosting(containerId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post(`/api/containers/${containerId}/hosting/restore`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["hosting", containerId] });
      qc.invalidateQueries({ queryKey: ["containers", containerId] });
      qc.invalidateQueries({ queryKey: ["containers"] });
    }
  });
}

/**
 * Returns the SSO-wrapped HostShop URL to redirect to. The caller
 * (the "Add hosting" card) does window.location.href on the result.
 */
export function useGetProvisioningUrl(containerId: number) {
  return useMutation({
    mutationFn: (body: ProvisioningUrlRequest) =>
      api.post<ProvisioningUrlResult>(`/api/containers/${containerId}/hosting/provisioning-url`, body)
  });
}
